// 203 §A.1 — THE THREE TABLE EXPORTERS, ONE MODULE FOR TWO SURFACES.
//
// Extracted VERBATIM from src/views/ReportsView.tsx (`today` · `fdE` · the
// EXPORT HELPERS block: `printHtml` · `exportWord` · `exportExcel`, its
// :290–457 at 671acb1e) so that Costs & Fees can export what is on screen
// through the same definition Reports uses — Laurent, 2026-09-21: "not to
// double the feature, it is to make it available while glancing". Nothing
// below was improved by that move (SESSION_RULES rule 3): the print helper's
// inline CSS, its @media print block and 181 ruling 1's filename rule (spaces,
// not '_') travelled unchanged, and the ReportsView call sites are the
// contract — `title`, `headers`, the rows as arrays of cell strings, and a
// summed total row.
//
// 203-glance §B + §C (2026-09-21) THEN DRESSED THE THREE OUTPUTS, deliberately
// for BOTH surfaces (the brief's §E: Reports' files change too — that is what
// Laurent asked for): the Word page turns landscape by a measured column
// count and declares its cell margins (§B); every output wears the firm's
// colour on its title, the tints derived from it (themeDerivation.ts's
// `deriveDocumentPalette`), the app's own faces named with a fallback, never
// embedded (§C). `ExportDress` below is what a caller hands over for that.
//
// `fdE` is exported too: ReportsView's row builders format their dates with
// it, and one definition beats two.
import { loadXlsx } from "./loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is written
import { xlsxAoa } from "./xlsxCells.mjs";
import { escapeHtml } from "./htmlEscape.mjs";
import { CT } from "./tokens";
import { deriveDocumentPalette } from "./themeDerivation";
import { SYSTEM_FONT, SYSTEM_FONT_STACK, normaliseAppFont } from "./appFonts";
import { styleXlsx } from "./xlsxStyle.mjs";

const today = () => new Date().toISOString().slice(0, 10);
const fdE = (d: any) =>
  d
    ? new Date(d).toLocaleDateString("fr-BE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

/* 203-glance §C [RULED — Laurent, 2026-09-21: "the firm's current fonts, same
   as used in the app, so the document is same style as the app entirely"; on
   Word fonts "name and fallback, we'll see" — NOT embedded] — THE DRESS every
   export wears, handed over by the caller: the firm's DAY theme, from which
   the palette is DERIVED (never a fill passed beside the colour), and the
   figure columns (the ones the screen right-aligns), which take the app's
   figure face. `boldCols` names the columns the screen sets bold outside the
   header and total rows (the Overheads sheet's TOTAL). */
export interface ExportDress {
  /** the firm's DAY theme's stored vars (`Theme.vars` of `FirmSettings.dayThemeId`, App's `dayThemeVarsOf`); null before the rows land — the derivation's own defaults then stand */
  theme: Record<string, string> | null;
  /** 203-glance-2 §C — the EXPORTING USER's text face as the app resolves the preference (App's `appFont`: one of APP_FONTS — "DM Sans", "Nunito Sans" or the system default); the document wears it */
  font: string;
  /** the columns the screen right-aligns — figures: the figure face, right-aligned in the file */
  rightCols: readonly number[];
  /** the columns the screen sets bold outside the header and total rows — Excel only */
  boldCols?: readonly number[];
  /** 204 §D — the row's identifying LEADING columns (Ref + Title, Name + Role), repeated in every block a
   *  wide table is split into so a row can be followed across them; absent, the first column alone */
  keyCols?: number;
  /** 204 §D — the KEY under the table: every code with its full name, in all three formats (the activity
   *  tables' heads are bare codes since §C, and a file has no hover to decode them) */
  legend?: ExportLegend;
}
export interface ExportLegend {
  /** the key's heading (« Activity codes ») — and, in Excel, the row that opens it */
  title: string;
  /** [code, full name], in the table's own column order */
  rows: readonly (readonly [string, string])[];
}
/** the app's figure face — `"JetBrains Mono", monospace` at App.css:381, :1013 and :1037 and on every mono cell; not a preference, it does not move (203-glance-2 §C) */
const FIGURE_FONT = "JetBrains Mono";
const FIGURE_STACK = `"${FIGURE_FONT}",monospace`;

/* 203-glance-2 §C [RULED — Laurent, 2026-09-21: "user font"] — THE DOCUMENT
   FOLLOWS THE PERSON EXPORTING. The app's text face is a per-user preference
   (`prefs.font`: DM Sans · Nunito Sans · the system default, appFonts.ts), and
   until this brief the documents named the app's DEFAULT whoever exported —
   a colleague reading in Nunito Sans exported a file in DM Sans. The face now
   rides the dress (`ExportDress.font`, threaded as the palette is; no second
   channel) and the three outputs take it from `facesOf` below: Word names it
   in every run and in the document default, Excel's styler names it, print's
   body wears its CSS chain. "SYSTEM DEFAULT" IS A REAL ARM, NOT A LABEL: that
   preference is a CSS stack of LOCAL faces (`SYSTEM_FONT_STACK`), which print
   can wear as it is — the same browser resolves it exactly as the app does —
   but a .docx and an .xlsx name ONE face, so the stack is RESOLVED, in the
   browser, to what it actually renders as: `resolveSystemFace` measures a
   sample under the stack on a canvas and returns the first NAMED family that
   (a) is installed here (its width differs from the generic fallback's) and
   (b) renders the sample at exactly the stack's width — 'Segoe UI' on Windows,
   Roboto where Android's face is installed, the platform's sans where the
   stack falls through to it. Where the stack renders a face no document can
   name — macOS's system font is reachable as `-apple-system` and nothing
   else — it returns null, and the document names NO text face: Word's and
   Excel's own default is then the honest fallback (Laurent: "name and
   fallback, we'll see"), never the word "System" and never a face the reader
   did not see. Measured once per page: local faces do not change under it. */
interface Faces {
  /** the text face by name for Word and Excel, or null when the system face cannot be named */
  name: string | null;
  /** the CSS chain the print document's body wears */
  stack: string;
  /** the faces to `fonts.load` into the print document before the dialogue: the app's web fonts alone (a system face is local) */
  load: string[];
}
const SYSTEM_CANDIDATES = ["Segoe UI", "Roboto", "Helvetica Neue", "Helvetica", "Arial", "Liberation Sans", "DejaVu Sans", "Noto Sans", "Ubuntu", "Cantarell"];
const SAMPLE = "The quick brown fox jumps over the lazy dog — 0123456789 Wxyz ÀÉÎ";
let systemFace: string | null | undefined;
const resolveSystemFace = (): string | null => {
  if (systemFace !== undefined) return systemFace;
  const ctx = document.createElement("canvas").getContext("2d");
  systemFace = null;
  if (!ctx) return null;
  const widthOf = (family: string): number => {
    ctx.font = `32px ${family}`;
    return ctx.measureText(SAMPLE).width;
  };
  const stack = widthOf(SYSTEM_FONT_STACK);
  const generic = widthOf("monospace");
  for (const name of SYSTEM_CANDIDATES) {
    const w = widthOf(`"${name}",monospace`);
    if (w === generic) continue; // not installed here: the fallback rendered
    if (w === stack) {
      systemFace = name; // installed, and what the stack renders as
      break;
    }
  }
  return systemFace;
};
const facesOf = (dress: ExportDress): Faces => {
  const font = normaliseAppFont(dress.font);
  if (font === SYSTEM_FONT) return { name: resolveSystemFace(), stack: SYSTEM_FONT_STACK, load: [] };
  // a named web face over the app's own system stack — the chain the print body wore for DM Sans since 203-glance §C, now for the reader's face
  return { name: font, stack: `"${font}",${SYSTEM_FONT_STACK}`, load: [font] };
};
/** a `#rrggbb` as docx and the Excel styler want it */
const hex6 = (c: string) => c.replace(/^#/, "");
const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** the app's own @font-face rules, read off the document's SAME-ORIGIN stylesheets
 *  (fonts.css as the browser resolved it — `cssText` serialises the URLs
 *  absolute), so the print document names the faces the app loads and not a
 *  copy of their declarations. A stylesheet from another origin refuses its
 *  rules and is not the app's: skipped by its href, never caught. */
const appFontFaces = (): string => {
  const out: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    if (sheet.href !== null && !sheet.href.startsWith(location.origin)) continue;
    for (const rule of Array.from(sheet.cssRules)) if (rule instanceof CSSFontFaceRule) out.push(rule.cssText);
  }
  return out.join("\n");
};

/** file-saver, once: the two downloads below share it */
const saveBlob = async (blob: Blob, name: string) => {
  const { saveAs } = await import("file-saver" as any);
  saveAs(blob, name);
};

// ============ EXPORT HELPERS ============

// Slice 04e — `tableHtml` is MARKUP this module just built and is interpolated
// raw; `title` and `firmName` are VALUES and are escaped. Both kinds of argument
// arrive at the same function, so the distinction is made at the boundary rather
// than left to whoever calls it next. The document is written into a same-origin
// iframe below (doc.write), so anything unescaped here parses in the LexCRM
// origin — this is not a PDF-only path.
// 203-glance §C — the print document wears the firm's colour on its title line
// and rule, the derived stripe on its even rows, and the app's faces: DM Sans
// for text over the app's system stack, JetBrains Mono for the figure cells —
// declared through the app's own @font-face rules (appFontFaces), and LOADED
// before the dialogue opens, or the page would print the fallback. The brief
// read the print path as already carrying them: it did not — the body named
// 'Segoe UI', Arial.
const printHtml = (title: any, tableHtml: any, firmName: any, dress: ExportDress) => {
  const firm = escapeHtml(firmName || "Legal Practice Manager");
  const t = escapeHtml(title);
  const { brand, stripe } = deriveDocumentPalette(dress.theme);
  const faces = facesOf(dress); // 203-glance-2 §C — the exporting user's face, the system stack as it is
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t}</title>
<style>@page{size:A4 landscape;margin:15mm}${appFontFaces()}
body{font-family:${faces.stack};font-size:9pt;color:${CT.c_222}}
.hdr{display:flex;justify-content:space-between;margin-bottom:12pt;padding-bottom:8pt;border-bottom:2pt solid ${brand}}
.hdr h1{font-size:14pt;color:${brand};margin:0}.hdr .meta{font-size:8pt;color:${CT.c_666};text-align:right}
table{width:100%;border-collapse:collapse;margin:8pt 0}th{text-align:left;font-size:7.5pt;text-transform:uppercase;letter-spacing:.4px;color:${CT.c_666};padding:4pt 6pt;border-bottom:1.5pt solid ${CT.c_ccc};white-space:nowrap}
td{padding:4pt 6pt;font-size:9pt;border-bottom:.5pt solid ${CT.c_eee}}tr:nth-child(even){background:${stripe}}
.r{text-align:right}td.r{font-family:${FIGURE_STACK}}.b{font-weight:700}.sum td{border-top:2pt solid ${CT.c_222};font-weight:700;padding-top:6pt}
table+table{margin-top:14pt}.lgt{font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:${CT.c_666};margin:14pt 0 2pt}
.legend{width:auto;margin:0}.legend tr:nth-child(even){background:none}.legend td{font-size:8pt;padding:1.5pt 10pt 1.5pt 0;border-bottom:none}.legend td.code{font-family:${FIGURE_STACK}}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>
<div class="hdr"><h1>${t}</h1><div class="meta">${firm}<br>${fdE(today())}</div></div>
${tableHtml}</body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;top:-9999px;left:-9999px;width:297mm;height:210mm;border:none";
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument!;
  doc.open();
  doc.write(html);
  doc.close();
  const go = () => {
    iframe.contentWindow!.focus();
    iframe.contentWindow!.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  };
  // the faces the document names, loaded into IT (the app's own cache answers), then the 300 ms this path always allowed
  void Promise.allSettled([
    ...faces.load.flatMap((f) => [doc.fonts.load(`9pt "${f}"`), doc.fonts.load(`bold 8pt "${f}"`)]),
    doc.fonts.load(`9pt "${FIGURE_FONT}"`),
  ]).then(() => {
    setTimeout(go, 300);
  });
};

/* 203-glance §B.1 [JUDGEMENT — advisor, overrulable at the next glance] — LANDSCAPE
   BY COLUMN COUNT, one rule inside the shared exporter so it serves Reports and
   Costs & Fees alike and nobody has to remember it when a tab grows a column.
   THE THRESHOLD IS MEASURED, NOT ROUND (measure-widths.js, 2026-09-21): every
   table this exporter serves, as rendered on the fixture, its cells measured in
   Chromium under the app's own faces at the export's sizes (8 pt bold headings,
   9 pt text, 9 pt mono figures); per column the LONGEST UNBREAKABLE TOKEN — the
   width under which Word breaks inside a word (« Partne / r », the glance's
   finding) — summed with the cell margins below, against A4's text widths
   (portrait 11906 − 2 × 1440 = 9026 twips, landscape 16838 − 2 × 1440 = 13958).
   Every table of ≤ 10 columns fits portrait (the closest: Reports' invoices at
   10 columns, 8370; clients at 8, 8670); every table of ≥ 11 exceeds it
   (Reports' client at 11, 9615; case at 12, 10620; the collaborator overview
   at 12, 10515) — so the threshold falls between 10 and 11. The one table the
   count turns needlessly is the Overheads sheet (14 columns, 6600: twelve
   three-character month columns), accepted — a year across the page reads
   better wide. The three 26-column tables exceed even landscape (22020 to
   29445): landscape is where they break least. */
const LANDSCAPE_FROM = 11;
/* 203-glance §B.2 — THE CELL MARGINS, DECLARED (<w:tblCellMar>) instead of
   left to Word's own minimum: the print path's own `td{padding:4pt 6pt}`
   (printHtml above), in twips — 80 top and bottom, 120 left and right. */
const CELL_MARGINS = { top: 80, bottom: 80, left: 120, right: 120 };

/* 204 §D — WORD COLUMN BLOCKS FOR THE WIDE TABLES [JUDGEMENT — advisor, overrulable at the
   glance: repeat the row's fixed leading columns in every block; stack the blocks down the page
   with a gap, never a page break each, so a short table stays one page]. THE DEFECT: the three
   26-column Costs & Fees tables carry more unbreakable content than landscape A4's text width
   holds (RE-MEASURED on this tree after §C turned the activity heads into codes, measure-widths.js
   on the fixture: matters 22 020 twips, by activity 19 245, per collaborator 19 335, against
   13 958 — the brief's 22 020 to 29 445 was the figure before §C), so Word squeezed every column
   under its content and a heading read one letter per line. THE CURE: the columns are split into
   BLOCKS that fit the page's text width, each block repeating the row's KEY columns
   (`ExportDress.keyCols`) so a row can be followed across them; each block is its own table with
   the header row and the totals row, the blocks stacked down the page with a gap. THE MEASURE IS
   TAKEN AT EXPORT TIME, IN THE BROWSER, ON THE DATA BEING EXPORTED: per column the longest
   unbreakable token (Word breaks a line at a space, and inside a word only when the word is wider
   than its cell — « Partne / r », the 203 glance's finding), measured on a canvas under the faces
   the file names at the sizes the file uses (heading 8 pt bold, text 9 pt, figures 9 pt in the
   figure face, the totals row bold), in twips, plus the declared cell margins — the method the
   203-glance threshold was measured with, now inside the exporter so the block count follows the
   data and never a figure typed here. A table whose columns fit is ONE block, as before; a page
   with no canvas to measure on is one block too. PRINT TAKES THE SAME SPLIT, computed once against
   the Word page (the print page is always landscape and wider by its narrower margins — 15 mm to
   Word's 25.4 — so a split that fits Word fits print), so the two outputs agree block for block.
   Excel is unbounded and is never split. */
const A4_TEXT_WIDTH = { portrait: 11906 - 2 * 1440, landscape: 16838 - 2 * 1440 } as const; // 9026 / 13958 twips — Word's 1440 margins, the sectPr the document writes
const TWIPS_PER_PX = 15; // 96 dpi: 1 px = 0.75 pt = 15 twips
const ptPx = (pt: number): number => (pt * 96) / 72;
/** the widest unbreakable token of `text` under a canvas font, in twips; 0 for nothing */
const widestToken = (ctx: CanvasRenderingContext2D, text: string, font: string): number => {
  ctx.font = font;
  let max = 0;
  for (const tok of text.split(/\s+/)) {
    if (!tok) continue;
    const w = ctx.measureText(tok).width;
    if (w > max) max = w;
  }
  return Math.round(max * TWIPS_PER_PX);
};
/** per column, the least width its content needs in twips, cell margins included; null where nothing can measure */
const columnMinWidths = (headers: string[], rows: unknown[][], totalRow: readonly (string | null)[] | null, dress: ExportDress): number[] | null => {
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return null;
  const faces = facesOf(dress);
  const head = `bold ${String(ptPx(8))}px ${faces.stack}`;
  const text = `${String(ptPx(9))}px ${faces.stack}`;
  const textBold = `bold ${String(ptPx(9))}px ${faces.stack}`;
  const figure = `${String(ptPx(9))}px ${FIGURE_STACK}`;
  const figureBold = `bold ${String(ptPx(9))}px ${FIGURE_STACK}`;
  const right = new Set(dress.rightCols);
  return headers.map((h, ci) => {
    let w = widestToken(ctx, h, head);
    for (const r of rows) w = Math.max(w, widestToken(ctx, String(r[ci] ?? ""), right.has(ci) ? figure : text));
    if (totalRow) w = Math.max(w, widestToken(ctx, totalRow[ci] ?? "", right.has(ci) ? figureBold : textBold));
    return w + CELL_MARGINS.left + CELL_MARGINS.right;
  });
};
/** the table's columns as BLOCKS that fit `textWidth`, each opening with the key columns — one block holding every column when they fit */
const columnBlocks = (headers: string[], rows: unknown[][], totalRow: readonly (string | null)[] | null, dress: ExportDress, textWidth: number): readonly (readonly number[])[] => {
  const n = headers.length;
  const all = headers.map((_, i) => i);
  const keyCols = Math.min(Math.max(dress.keyCols ?? 1, 0), n);
  const widths = columnMinWidths(headers, rows, totalRow, dress);
  if (widths === null) return [all];
  if (widths.reduce((sum, w) => sum + w, 0) <= textWidth) return [all];
  const keys = all.slice(0, keyCols);
  const keyWidth = keys.reduce((sum, i) => sum + widths[i], 0);
  const blocks: number[][] = [];
  let block: number[] = [];
  let used = keyWidth;
  for (let i = keyCols; i < n; i++) {
    // a column wider than the room left beside the keys still gets a block of its own: nothing else can be done for it
    if (block.length > 0 && used + widths[i] > textWidth) {
      blocks.push([...keys, ...block]);
      block = [];
      used = keyWidth;
    }
    block.push(i);
    used += widths[i];
  }
  if (block.length > 0) blocks.push([...keys, ...block]);
  return blocks.length ? blocks : [all];
};

/* 203-glance §C — THE WORD DOCUMENT'S DRESS: the title in the firm's colour,
   the header cells filled with it under the DERIVED ink, the even rows tinted
   with the derived stripe (the `2a5db0` / `F0F2F5` / white literals the file
   carried are gone); the app's faces NAMED — `w:rFonts` takes one name per
   slot and Word substitutes when the face is absent (Laurent: "name and
   fallback, we'll see"; docx 9.6.1 can write a font-table entry for an
   EMBEDDED face alone, and embedding is not this brief); the figure columns
   in the figure face, right-aligned as on screen and in print; the TOTAL ROW
   its own row, bold as on screen and in print — it used to travel as a plain
   data row the caller appended. */
const exportWord = async (title: any, headers: any, rows: any, firmName: any, totalRow: readonly (string | null)[] | null, dress: ExportDress) => {
  const {
    Document,
    Packer,
    PageOrientation,
    Paragraph,
    TextRun,
    /* 188 B.3 — aliased: `Table` at module scope is now the app's table shell
       (components/Table), mounted below; docx's class keeps its name inside this
       function under another binding, so one name means one thing in this file
       (the 187 (2) class — a call result is a received bag to the guard). */
    Table: DocxTable,
    TableRow,
    TableCell,
    WidthType,
    AlignmentType,
    ShadingType,
  } = await import("docx");
  const firm = firmName || "Legal Practice Manager";
  const { brand, ink, stripe } = deriveDocumentPalette(dress.theme);
  // 203-glance-2 §C — the exporting user's face; NO name when the system face cannot be named (Word's own default stands)
  const text = facesOf(dress).name;
  const textFace = text === null ? {} : { font: text };
  const rightCols = new Set(dress.rightCols);
  const headerTexts = headers as string[];
  const bodyRows = rows as unknown[][];
  // a figure column is right-aligned in every row; its FIGURES take the figure face, its heading the text face (as the screen's <th>)
  const cellOf = (text: string, ci: number, run: { bold?: boolean; size: number; color?: string }, heading: boolean, shading?: string) =>
    new TableCell({
      children: [
        new Paragraph({
          alignment: rightCols.has(ci) ? AlignmentType.RIGHT : AlignmentType.LEFT,
          children: [new TextRun({ text, ...(rightCols.has(ci) && !heading ? { font: FIGURE_FONT } : textFace), ...run })],
        }),
      ],
      ...(shading !== undefined ? { shading: { type: ShadingType.SOLID, color: shading } } : {}),
    });

  // §B.1 — the page turns with the column count; the margins stay Word's 1440 twips
  const landscape = headerTexts.length >= LANDSCAPE_FROM;
  /* 204 §D — one table per BLOCK of columns (one block when they fit): each carries the header row,
     every data row and the totals row for its own columns, `ci` staying the ORIGINAL column index so a
     figure column keeps its face and alignment wherever its block puts it. */
  const blocks = columnBlocks(headerTexts, bodyRows, totalRow, dress, landscape ? A4_TEXT_WIDTH.landscape : A4_TEXT_WIDTH.portrait);
  const tableOf = (cols: readonly number[]) =>
    new DocxTable({
      rows: [
        new TableRow({ children: cols.map((ci) => cellOf(headerTexts[ci], ci, { bold: true, size: 16, color: hex6(ink) }, true, hex6(brand))) }),
        ...bodyRows.map(
          (row, ri) =>
            new TableRow({
              children: cols.map((ci) => cellOf(String(row[ci] ?? ""), ci, { size: 18 }, false, ri % 2 === 1 ? hex6(stripe) : undefined)),
            }),
        ),
        ...(totalRow ? [new TableRow({ children: cols.map((ci) => cellOf(totalRow[ci] ?? "", ci, { bold: true, size: 18 }, false)) })] : []),
      ],
      width: { size: 100, type: WidthType.PERCENTAGE },
      margins: { marginUnitType: WidthType.DXA, ...CELL_MARGINS }, // §B.2
    });
  // the GAP between two blocks: an empty paragraph (two adjacent tables with nothing between them MERGE in Word), never a page break
  const gap = () => new Paragraph({ spacing: { before: 120, after: 120 }, children: [] });
  const blockChildren = blocks.flatMap((cols, bi) => (bi === 0 ? [tableOf(cols)] : [gap(), tableOf(cols)]));
  /* 204 §D — THE KEY under the last block: the title as the firm/date lines are dressed, then code · name
     rows, the code in the figure face (the chip's own), the table as wide as its content. */
  const legend = dress.legend;
  const legendChildren = legend && legend.rows.length
    ? [
        new Paragraph({ spacing: { before: 240, after: 80 }, children: [new TextRun({ text: legend.title, bold: true, size: 16, color: "888888", ...textFace })] }),
        new DocxTable({
          rows: legend.rows.map(
            ([code, name]) =>
              new TableRow({
                children: [
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: code, font: FIGURE_FONT, size: 16 })] })] }),
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: name, size: 16, ...textFace })] })] }),
                ],
              }),
          ),
          width: { size: 0, type: WidthType.AUTO },
          margins: { marginUnitType: WidthType.DXA, ...CELL_MARGINS },
        }),
      ]
    : [];

  const doc = new Document({
    // the document's own default face: anything a run below does not name reads the text face (none named: Word's own default)
    styles: { default: { document: { run: textFace } } },
    sections: [
      {
        properties: landscape ? { page: { size: { orientation: PageOrientation.LANDSCAPE } } } : {},
        children: [
          new Paragraph({
            alignment: AlignmentType.LEFT,
            children: [new TextRun({ text: firm, size: 20, color: "888888", ...textFace })],
          }),
          new Paragraph({
            alignment: AlignmentType.LEFT,
            spacing: { after: 100 },
            children: [
              new TextRun({
                text: title,
                bold: true,
                size: 28,
                color: hex6(brand),
                ...textFace,
              }),
            ],
          }),
          new Paragraph({
            spacing: { after: 200 },
            children: [
              new TextRun({ text: fdE(today()), size: 18, color: "888888", ...textFace }),
            ],
          }),
          ...blockChildren, // 204 §D — the blocks, a gap between them
          ...legendChildren, // 204 §D — the key
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  await saveBlob(blob, `${title} ${today()}.docx`); // 181 (ruling 1) — spaces, not '_'
};

/* 203-glance §C.2 — THE EXCEL'S FIRST STYLING: the workbook SheetJS writes,
   as bytes, dressed by xlsxStyle.mjs (the header row filled with the firm's
   colour under the derived ink, the app's faces, the figure columns in the
   figure face right-aligned, bold where the screen is bold) and saved under
   the same name. The column widths written below stay as written.
   203-glance §D [JUDGEMENT — advisor, overrulable] — THE TOTALS ROW, in the
   Excel too: Word and print already carried it, and a figure present in two
   formats of three is the kind that gets re-added by hand. `totalRow` is the
   shape the other two take (the label first, a figure per summed column, null
   where a column sums nothing → an empty cell); it lands LAST, bold as on
   screen (the styler's `totalRow`). The four Costs & Fees data tables pass
   theirs, and so does Reports (203-glance-2 §D [RULED]: its Word and print
   carried one, its Excel now does too, from this same code); the Overheads
   sheet passes null — its Excel is §B's import template and its row-1
   contract (« Catégorie · TOTAL · 1 … 12 », no totals row) is what the
   importer reads back. */
const exportExcel = async (title: any, headers: any, rows: any, totalRow: readonly (string | null)[] | null, dress: ExportDress) => {
  // LEGAL-LANE (round 2, Excel sweep) — an array-of-arrays sheet built from
  // report rows: descriptions and note columns make it unbounded. Guarded with
  // xlsxAoa, and a throw now reaches the user instead of the console.
  try {
  const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
  const body = rows as unknown[][];
  // a null of the totals row is an ABSENT cell (as the template's empties are), never "": an empty string blocks Excel's overflow
  const sheetRows = totalRow ? [...body, totalRow.map((v) => v ?? null)] : body;
  /* 204 §D — THE KEY on the same sheet, under the totals row after one empty row: the title alone on its
     row, then code · name. Never a second sheet — the styler (xlsxStyle.mjs) dresses EVERY worksheet with
     the table's own rules (row 1 the header, the figure columns, the totals row), which a key is not. The
     rows above keep their indexes, so `totalRow` below still names the totals row. */
  const legendRows: unknown[][] = dress.legend && dress.legend.rows.length ? [[], [dress.legend.title], ...dress.legend.rows.map(([code, name]) => [code, name])] : [];
  const data = xlsxAoa([headers, ...sheetRows, ...legendRows]);
  const ws = XLSX.utils.aoa_to_sheet(data);
  // Auto-width — over every row the sheet carries, the totals row included
  const colW = headers.map((h: any, i: any) => {
    let max = h.length;
    sheetRows.forEach((r) => {
      const v = String(r[i] ?? "");
      if (v.length > max) max = v.length;
    });
    return { wch: Math.min(max + 2, 40) };
  });
  ws["!cols"] = colW;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
  const raw = XLSX.write(wb, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
  const { brand, ink } = deriveDocumentPalette(dress.theme);
  // the container re-packed by SheetJS's own CFB (handed in: the styler names no parser of its own)
  const styled = styleXlsx(raw, {
    textFont: facesOf(dress).name, // 203-glance-2 §C — the exporting user's face; null keeps Excel's own default face

    figureFont: FIGURE_FONT,
    headerFill: hex6(brand),
    headerInk: hex6(ink),
    rightCols: dress.rightCols,
    boldCols: dress.boldCols ?? [],
    totalRow: totalRow ? body.length + 2 : null, // 1-based: the header is row 1, the data rows 2 … n + 1
  }, XLSX.CFB);
  // CFB hands back a Uint8Array over its own ArrayBuffer; lib.dom's BlobPart wants that said
  await saveBlob(new Blob([styled as Uint8Array<ArrayBuffer>], { type: XLSX_MIME }), `${title} ${today()}.xlsx`); // 181 (ruling 1) — spaces, not '_'
  } catch (e: any) {
    const w = window as any;
    const say = w.__lxToast || w.lxToast;
    if (say) say("⚠ Export failed: " + (e?.message || "unknown error"));
    else throw e;
  }
};

/* 203 §A.2.0 [A CURE] — THE TABLE'S MARKUP FOR THE PRINT PATH, ONCE. Written
   FROM ReportsView's `doPdf` (:1565–1608 at 5ac4c52a) and byte-identical in
   what it prints: the header row with the `r` class on the right-aligned
   columns, one `<tr>` per row, and — when the table has one — the summed
   total row in the `<tfoot>` as `tr.sum`, its first cell the label, a cell
   with no figure empty. Every VALUE is escaped at this boundary (04e's rule;
   the oracle reads these lines); the scaffolding is literal. `totalRow` is
   the shape `doWord` already hands `exportWord` — the label first, then a
   figure per summed column — with `null` where a column sums nothing.
   ReportsView's `doPdf` calls this; the five Costs & Fees tables (A.2) call
   it too, so there is one print path and not two. 203-glance §C: the figure
   columns are the dress's `rightCols`, the theme and the exporting user's
   face ride in it too. */
const printTable = (
  title: string,
  headers: string[],
  rows: unknown[][],
  totalRow: (string | null)[] | null,
  firmName: string,
  dress: ExportDress,
) => {
  const rightCols = dress.rightCols;
  /* 204 §D — THE SAME SPLIT AS THE WORD DOCUMENT'S, one `<table>` per block (the gap is the
     stylesheet's `table+table`), `i` the ORIGINAL column index throughout; the key after the last. */
  const blocks = columnBlocks(headers, rows, totalRow, dress, headers.length >= LANDSCAPE_FROM ? A4_TEXT_WIDTH.landscape : A4_TEXT_WIDTH.portrait);
  const tableOf = (cols: readonly number[]): string => {
    // `h` and `c` are the names 04e's oracle reads the escaped interpolations under (recordings/04e-html-escaping): the source is asserted, not a reproduction of it
    const thRow = cols
      .map((i) => {
        const h = headers[i];
        return `<th class="${rightCols.includes(i) ? "r" : ""}">${escapeHtml(h)}</th>`;
      })
      .join("");
    const bodyRows = rows
      .map(
        (r) =>
          "<tr>" +
          cols
            .map((i) => {
              const c = r[i];
              return `<td class="${rightCols.includes(i) ? "r" : ""}">${escapeHtml(c ?? "")}</td>`;
            })
            .join("") +
          "</tr>",
      )
      .join("");
    const sumRow = totalRow
      ? '<tr class="sum">' +
        cols
          .map((i) => {
            const v = totalRow[i];
            if (i === 0) return `<td>${escapeHtml(v ?? "")}</td>`;
            if (v != null) return `<td class="r">${escapeHtml(v)}</td>`;
            return "<td></td>";
          })
          .join("") +
        "</tr>"
      : "";
    return `<table><thead><tr>${thRow}</tr></thead><tbody>${bodyRows}</tbody><tfoot>${sumRow}</tfoot></table>`;
  };
  const legend = dress.legend;
  const legendHtml = legend && legend.rows.length
    ? `<p class="lgt">${escapeHtml(legend.title)}</p><table class="legend">${legend.rows.map(([code, name]) => `<tr><td class="code">${escapeHtml(code)}</td><td>${escapeHtml(name)}</td></tr>`).join("")}</table>`
    : "";
  printHtml(title, blocks.map(tableOf).join("") + legendHtml, firmName, dress);
};

export { fdE, printHtml, exportWord, exportExcel, printTable };
